const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;

export function pad2(n) {
  return String(n).padStart(2, '0');
}

/**
 * Parse stored event/close values.
 * - Date only: "YYYY-MM-DD"
 * - Date + time: "YYYY-MM-DDTHH:mm" (or ISO with offset/Z)
 */
export function parseDateTimeValue(value) {
  if (value == null || value === '') {
    return { date: '', time: '', hasTime: false };
  }

  const str = String(value).trim();
  if (!str) return { date: '', time: '', hasTime: false };

  const dateOnly = str.match(DATE_ONLY_RE);
  if (dateOnly) {
    return { date: str, time: '', hasTime: false };
  }

  const withTime = str.match(DATE_TIME_RE);
  if (withTime) {
    // Prefer local wall-clock for offset/Z ISO strings
    if (/Z|[+-]\d{2}:?\d{2}$/.test(str)) {
      const d = new Date(str);
      if (!Number.isNaN(d.getTime())) {
        return {
          date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
          time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
          hasTime: true,
        };
      }
    }
    return {
      date: `${withTime[1]}-${withTime[2]}-${withTime[3]}`,
      time: `${withTime[4]}:${withTime[5]}`,
      hasTime: true,
    };
  }

  const d = new Date(str);
  if (!Number.isNaN(d.getTime())) {
    return {
      date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
      time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
      hasTime: true,
    };
  }

  return { date: '', time: '', hasTime: false };
}

export function composeDateTimeValue(date, time, includeTime) {
  if (!date) return '';
  if (!includeTime) return date;
  const safeTime = /^\d{2}:\d{2}$/.test(time || '') ? time : '12:00';
  return `${date}T${safeTime}`;
}

export function parseLocalDateTime(value) {
  const { date, time, hasTime } = parseDateTimeValue(value);
  if (!date) return null;
  const [year, month, day] = date.split('-').map(Number);
  if (hasTime && time) {
    const [hour, minute] = time.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute, 0, 0);
  }
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

export function formatDateTimeDisplay(value, { fallback = '' } = {}) {
  const parsed = parseDateTimeValue(value);
  if (!parsed.date) return fallback;
  const d = parseLocalDateTime(value);
  if (!d || Number.isNaN(d.getTime())) return fallback;

  if (!parsed.hasTime) {
    return d.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  return d.toLocaleString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function toDatetimeLocalValue(isoOrEmpty) {
  const parsed = parseDateTimeValue(isoOrEmpty);
  if (!parsed.date) return '';
  if (!parsed.hasTime) return parsed.date;
  return `${parsed.date}T${parsed.time}`;
}

export function getMonthGrid(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  let startDow = first.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1; // Monday-first
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export const IT_WEEKDAY_SHORT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export const IT_MONTH_NAMES = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];
